// Card image cache utility
class ImageCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.preloadStarted = false;
  }

  // Preload an image and cache it
  async preloadImage(url) {
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const loadPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.cache.set(url, url); // Cache the URL as loaded
        this.loadingPromises.delete(url);
        resolve(url);
      };
      img.onerror = () => {
        this.loadingPromises.delete(url);
        reject(new Error(`Failed to load image: ${url}`));
      };
      img.src = url;
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  // Get cached image URL (returns immediately if cached)
  getCachedImage(url) {
    return this.cache.get(url) || url;
  }

  // Check if image is cached
  isImageCached(url) {
    return this.cache.has(url);
  }

  // Preload all card images
  async preloadAllCardImages() {
    if (this.preloadStarted) {
      return;
    }
    
    this.preloadStarted = true;

    const cardImageUrls = [
      '/card-images/1.jpg',
      '/card-images/2.jpg',
      '/card-images/3.jpg',
      '/card-images/4.jpg',
      '/card-images/5.jpg',
      '/card-images/11.jpg',
      '/card-images/12.jpg',
      '/card-images/13.jpg',
      '/card-images/14.jpg',
      '/card-images/15.jpg',
      '/card-images/16.jpg',
      '/card-images/17.jpg',
      '/card-images/18.jpg',
      '/card-images/19.jpg',
      '/card-images/111.jpg',
      '/card-images/112.jpg',
      '/card-images/113.jpg',
      '/card-images/114.jpg',
      '/card-images/115.jpg',
      '/card-images/116.jpg',
      '/card-images/121.jpg',
      '/card-images/122.jpg',
      '/card-images/123.jpg',
      '/card-images/124.jpg',
      '/card-images/125.jpg',
      '/card-images/126.jpg',
      '/card-images/127.jpg',
      '/card-images/128.jpg',
      '/card-images/129.jpg',
      '/card-images/141.jpg',
      '/card-images/142.jpg',
      '/card-images/143.jpg',
      '/card-images/144.jpg',
      '/card-images/145.jpg',
      '/card-images/161.jpg',
      '/card-images/162.jpg',
      '/card-images/163.jpg',
      '/card-images/164.jpg',
      '/card-images/165.jpg',
      '/card-images/166.jpg',
      '/card-images/167.jpg',
      '/card-images/168.jpg',
      '/card-images/191.jpg',
      '/card-images/192.jpg',
      '/card-images/193.jpg'
    ];

    // Preload images in batches to avoid overwhelming the browser
    const batchSize = 5;
    for (let i = 0; i < cardImageUrls.length; i += batchSize) {
      const batch = cardImageUrls.slice(i, i + batchSize);
      try {
        await Promise.all(batch.map(url => this.preloadImage(url)));
        console.log(`Preloaded card images batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(cardImageUrls.length / batchSize)}`);
      } catch (error) {
        console.warn('Some images in batch failed to preload:', error);
      }
    }

    console.log('All card images preloaded and cached');
  }

  // Start preloading in background (non-blocking)
  startBackgroundPreload() {
    // Use setTimeout to avoid blocking the main thread
    setTimeout(() => {
      this.preloadAllCardImages().catch(error => {
        console.warn('Background image preload failed:', error);
      });
    }, 100);
  }
}

// Create a singleton instance
const imageCache = new ImageCache();

export default imageCache;